import {
  Box,
  chakra,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Stack,
  useMediaQuery,
  useToast,
} from "@chakra-ui/react";
import {
  EventDetailAttendeesSection,
  EventDetailTimelinesSection,
  KYDAdminEventDetailSectionsTypeEnum,
} from "common/interfaces/KYDEvent";
import { EmailType, emails } from "common/email-templates";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { InstantSearch } from "react-instantsearch";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import dayjs from "dayjs";

import actions from "src/actions";
import { KYDOrganizationUser } from "common/interfaces/KYDUser";
import ReschedulingCampaignModalCampaign from "./ReschedulingCampaignModalCampaign";
import { ReschedulingCampaignSchema, ReschedulingFormValues } from "./schema";
import { Spinner } from "@chakra-ui/react";
import { formatDateForCampaign } from "src/utils";
import ReschedulingCampaginEmailPreview from "./ReschedulingCampaginEmailPreview";
import { KYDOrganizationFull } from "@common/interfaces/KYDOrganization";
import { useAlgoliaClient } from "../hooks/useAlgoliaClient";
import { render } from "src/Manager/Campaigns/utils/emailRenderer";

const emailType = EmailType.EventRescheduled;
const KydForm = chakra(chakra.form);

const ReschedulingCampaignModal = ({
  data,
  onCancel,
  onSave,
  isOpen = false,
  onSend,
  updatedStartAt,
}: {
  data?: EventDetailTimelinesSection["data"];
  onCancel: () => void;
  onSave: (data: ReschedulingFormValues) => Promise<any>;
  isOpen: boolean;
  onSend: (event_id: string, data: ReschedulingFormValues) => Promise<any>;
  updatedStartAt: string;
}) => {
  const [showPreview, setShowPreview] = useState(false);
  const [emailHtml, setEmailHtml] = useState<string | null>(null);
  const [organizationUsers, setOrganizationUsers] = useState<
    KYDOrganizationUser[] | null
  >(null);
  const [organization, setOrganization] = useState<KYDOrganizationFull | null>(
    null
  );
  const [attendeesData, setAttendeesData] = useState<
    EventDetailAttendeesSection["data"] | null
  >(null);

  const [loading, setLoading] = useState({
    attendees: false,
    organizationUsers: false,
    reschedulingCampaign: false,
  });

  const [user, setUser] = useState<KYDOrganizationUser | null>(null);
  const { event_id } = useParams();
  const toast = useToast();
  const [view, setView] = useState<"desktop" | "mobile">("desktop");
  const isMobile = useMediaQuery("(max-width: 768px)")[0];

  const form = useForm<ReschedulingFormValues>({
    resolver: yupResolver(ReschedulingCampaignSchema),
    defaultValues: {
      additional_audiences: [],
      subject: `Rescheduled: ${data?.event_name}`,
      subject_preview: `Rescheduled: ${data?.event_name}`,
      message_from_venue: `Hey {{first_name}}, \n We've got an important update about your {{event_name}} ticket! The show originally scheduled for {{previous_start_at}} has been rescheduled to {{updated_start_at}}. Your current ticket will automatically be valid for the new date. \nUpdated ticket details below.`,
      message_from_venue_preview: `Hey ${user?.first_name ?? "[First Name]"}, \n We've got an important update about your ${data?.event_name} ticket! The show originally scheduled for ${formatDateForCampaign(data?.start_at, data?.timezone)} has been rescheduled to ${formatDateForCampaign(updatedStartAt, data?.timezone)}. Your current ticket will automatically be valid for the new date. \nUpdated ticket details below.`,
      additional_message: "",
      test_email_recipients: [],
      simulated_attendee_id: null,
      preheader: ``,
    },
    mode: "onChange",
    criteriaMode: "all",
    reValidateMode: "onChange",
  });

  const { reset, handleSubmit, watch } = form;
  const additional_message = watch("additional_message");
  const preheader = watch("preheader");
  const subject_preview = watch("subject_preview");
  const message_from_venue_preview = watch("message_from_venue_preview");

  const algolia = useAlgoliaClient(
    attendeesData?.algolia,
    {},
    {
      showResultsByDefault: true,
    }
  );

  // if the event is rescheduled more than 14 days from now, use the new date, otherwise use 2 weeks from now
  const last_date_for_refund =
    dayjs(updatedStartAt).diff(dayjs(), "days") < 14
      ? dayjs(updatedStartAt)
          .tz(data?.timezone)
          .format("ddd, MMMM D [at] h:mmA")
      : dayjs()
          .add(2, "weeks")
          .tz(data?.timezone)
          .format("ddd, MMMM D [at] h:mmA");

  const fetchData = useCallback(async () => {
    if (!event_id) return;
    setLoading((prev) => ({ ...prev, attendees: true }));

    try {
      const response = await actions.ADMIN.fetchEventSections(event_id, [
        KYDAdminEventDetailSectionsTypeEnum.ATTENDEES,
      ]);

      setAttendeesData(response.attendees.data);
    } catch (err) {
      console.error(err);
      toast({
        title: "Error",
        description: err.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading((prev) => ({ ...prev, attendees: false }));
    }
  }, [event_id, toast]);

  const fetchOrganizationUsers = useCallback(
    async (silent?: boolean) => {
      if (!silent) {
        setLoading((prev) => ({ ...prev, organizationUsers: true }));
      }
      let users = null;
      try {
        const response = await actions.ADMIN.fetchOrganizationUsers();
        setOrganizationUsers(response.users);
        users = response.users;
      } catch (err) {
        toast({
          title: "Error",
          description: err.message,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
      setLoading((prev) => ({ ...prev, organizationUsers: false }));
      return users;
    },
    [toast]
  );

  const onSubmit: SubmitHandler<any> = async (formData) => {
    // we are seperating the test data and preview data from real data before sending to the backend
    const {
      test_email_recipients,
      simulated_attendee_id,
      preview_value,
      subject_preview,
      message_from_venue_preview,
      ...rest
    } = formData;

    const payload = {
      previous_start_at: data?.start_at,
      updated_start_at: updatedStartAt,
      ...rest,
    };

    onSave(payload);
  };

  const onTestEmail = async () => {
    const formData = form.getValues();
    const payload = {
      ...formData,
      updated_start_at: updatedStartAt,
      previous_start_at: data?.start_at,
      is_test: true,
    };
    const res = await onSend(event_id, payload);
    if (res) {
      toast({
        title: "Test email sent",
        status: "success",
      });
    }
  };

  const onCloseModal = () => {
    onCancel();
    reset();
  };

  const fetchOrganization = useCallback(async () => {
    const response = await actions.ADMIN.fetchOrganization();
    setOrganization(response);
  }, []);

  const fetchMe = useCallback(async () => {
    try {
      const me = await actions.ADMIN.fetchMe();

      // if user data loads later than the message_from_venue_preview, we need to update the message_from_venue_preview
      const message_from_venue_preview = form.getValues(
        "message_from_venue_preview"
      );
      if (message_from_venue_preview.includes("[First Name]")) {
        const updated_message_from_venue_preview =
          message_from_venue_preview.replace("[First Name]", me.first_name);
        form.setValue(
          "message_from_venue_preview",
          updated_message_from_venue_preview
        );
      }
      setUser(me);
    } catch (err) {
      console.error(err);
    }
  }, [form]);

  useEffect(() => {
    const renderEmail = async () => {
      if (!data) return;
      const PreviewEmail = emails[emailType].template;

      const { response: emailHtml } = await render({
        template: PreviewEmail.toString(),
        version: 1,
        props: {
          event_name: data.event_name,
          event_url: data.link,
          event_image: data.image,
          display_previous_start_at: dayjs(data.start_at)
            .tz(data.timezone)
            .format("ddd, MMMM D [at] h:mmA"),
          display_updated_start_at: formatDateForCampaign(
            updatedStartAt,
            data.timezone
          ),
          display_updated_start_at_date: formatDateForCampaign(
            updatedStartAt,
            data.timezone
          ),
          first_name: user?.first_name || "[First Name]",
          last_name: user?.last_name || "[Last Name]",
          venue_name: data.venue.name,
          venue_address: data.venue.address,
          last_date_for_refund: last_date_for_refund,
          message_from_venue: message_from_venue_preview,
          additional_message: additional_message,
          preheader: preheader,
          ticket_type_names: data.ticket_types[0]?.name || "General Admission",
          quantity: 2,
          force_desktop: view === "desktop",
          related_shows: data.related_shows,
          organization_website: organization?.bio_link,
          socials: organization?.socials,
        },
      });
      setEmailHtml(emailHtml);
    };
    renderEmail();
  }, [
    data,
    updatedStartAt,
    message_from_venue_preview,
    additional_message,
    preheader,
    last_date_for_refund,
    view,
    user,
    organization,
  ]);

  useEffect(() => {
    fetchData();
    fetchOrganizationUsers();
    fetchMe();
    fetchOrganization();
  }, [fetchData, fetchOrganizationUsers, fetchMe, fetchOrganization]);

  return (
    <Modal
      size="full"
      isOpen={isOpen}
      onClose={onCloseModal}
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <ModalContent borderRadius="0">
        <KydForm w="full" onSubmit={handleSubmit(onSubmit)}>
          <ModalBody p="0">
            <Stack
              h="100%"
              w="100%"
              direction={{ base: "column", md: "row" }}
              overflowY={"hidden"}
            >
              <FormProvider {...form}>
                <Box
                  w={isMobile ? "100%" : "443px"}
                  bg="white"
                  overflowY={"auto"}
                >
                  {algolia.algoliaClient && user ? (
                    <InstantSearch
                      key={algolia.version}
                      future={{
                        preserveSharedStateOnUnmount: false,
                      }}
                      indexName={attendeesData?.algolia.index_name}
                      searchClient={algolia.algoliaClient}
                    >
                      <ReschedulingCampaignModalCampaign
                        user={user}
                        isMobile={isMobile}
                        showPreview={showPreview}
                        setShowPreview={setShowPreview}
                        data={data}
                        updatedStartAt={updatedStartAt}
                        organizationUsers={organizationUsers}
                        onCloseModal={onCloseModal}
                        onTestEmail={onTestEmail}
                        loading={
                          loading.attendees ||
                          loading.organizationUsers ||
                          loading.reschedulingCampaign
                        }
                      />
                    </InstantSearch>
                  ) : (
                    <Flex justifyContent="center" alignItems="center" h="100%">
                      <Spinner />
                    </Flex>
                  )}
                </Box>

                <ReschedulingCampaginEmailPreview
                  isMobile={isMobile}
                  showPreview={showPreview}
                  subject_preview={subject_preview}
                  emailHtml={emailHtml}
                  view={view}
                  setView={setView}
                />
              </FormProvider>
            </Stack>
          </ModalBody>
        </KydForm>
      </ModalContent>
    </Modal>
  );
};

export default ReschedulingCampaignModal;
